// src/pages/About.tsx
import { Link } from "react-router-dom";
import HeroMedia from "@/components/HeroMedia"; 
// If your tsconfig doesn't have the @ alias, use: 
// import HeroMedia from "../components/HeroMedia"; 
// (adjust the relative path to match your structure)

export default function AboutPage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Hero */}
      <section className="mx-auto max-w-6xl px-4 pt-16 pb-10">
        <div className="grid items-center gap-8 md:grid-cols-2">
          <div>
            <p className="text-xs uppercase tracking-wider text-primary/70">
              About Siimply
            </p>
            <h1 className="mt-2 text-3xl font-extrabold md:text-5xl">
              Invest smarter. <span className="text-primary">Simply.</span>
            </h1>
            <p className="mt-4 text-muted-foreground md:text-lg">
              Siimply is building a modern, self-directed platform for U.S.
              stocks and ETFs—made for new investors and power users alike.
              Clear pricing, intuitive tools, and education baked in.
            </p>
            <div className="mt-6 flex gap-3">
              <Link
                to="/simulator"
                className="rounded-xl bg-primary px-5 py-3 text-white hover:opacity-90"
              >
                Try the Trading Simulator
              </Link>
              <Link
                to="/charts"
                className="rounded-xl border px-5 py-3 hover:bg-accent/40"
              >
                Explore Market Charts
              </Link>
            </div>
            <p className="mt-3 text-xs text-muted-foreground">
              *Paper trading only. Real trading coming soon.
            </p>
          </div>

          {/* Hero art */}
<div className="relative hidden md:block">
  <HeroMedia />
</div>
        </div>
      </section>

      {/* Social proof / logos */}
      <section className="bg-muted/30 py-8">
        <div className="mx-auto max-w-6xl px-4">
          <p className="mb-4 text-center text-xs uppercase tracking-wider text-muted-foreground">
            As seen in / Backed by (placeholders)
          </p>
          <div className="grid grid-cols-2 items-center gap-6 opacity-70 md:grid-cols-6">
            {["TechCrunch", "Forbes", "BetaKit", "Product Hunt", "StackShare", "FinTech Global"].map(
              (name) => (
                <div
                  key={name}
                  className="h-10 rounded-md bg-background/60 ring-1 ring-border grid place-items-center text-xs"
                >
                  {name}
                </div>
              )
            )}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="mx-auto max-w-6xl px-4 py-12">
        <div className="grid gap-6 sm:grid-cols-3">
          <Stat kpi="$0" label="paper trade commissions" />
          <Stat kpi="5 min" label="average sign-up time" />
          <Stat kpi="24/7" label="education & help center" />
        </div>
      </section>

      {/* Why Siimply */}
      <section className="mx-auto max-w-6xl px-4 pb-8">
        <div className="grid gap-6 md:grid-cols-3">
          <Card
            title="Built for clarity"
            body="Plain language, transparent fees, and interfaces that make sense.
            We remove noise so you can focus on decisions."
          />
          <Card
            title="Serious tools"
            body="Institution-grade charts, screeners, and a realistic simulator
            to practice strategies before risking real money."
          />
          <Card
            title="Education first"
            body="On-chart tips, explainers, and nudges help you learn while you
            invest—no paywall, no fluff."
          />
        </div>
      </section>

      {/* Compliance / Security (keep it high-level for now) */}
      <section className="mx-auto max-w-6xl px-4 py-12">
        <div className="rounded-2xl border bg-card p-6 md:p-8">
          <h2 className="text-2xl font-bold">Security & Compliance</h2>
          <p className="mt-3 text-muted-foreground">
            We partner with regulated infrastructure providers to keep your data
            and activity safe. All traffic is encrypted, credentials are stored
            securely, and we follow industry best practices for authentication
            and monitoring. Real-trading features will launch with full
            disclosures and eligibility checks.
          </p>
          <ul className="mt-4 list-disc space-y-1 pl-6 text-sm text-muted-foreground">
            <li>Secure authentication with Supabase</li>
            <li>Role-based access to trading features</li>
            <li>Granular audit logs and alerting (internal)</li>
          </ul>
        </div>
      </section>

      {/* Story */}
      <section className="mx-auto max-w-6xl px-4 py-12">
        <h2 className="text-2xl font-bold">Our Story</h2>
        <div className="mt-6 grid gap-6 md:grid-cols-3">
          <TimelineItem
            year="2024"
            text="We started Siimply to make investing clear and approachable for
            everyone—without sacrificing power."
          />
          <TimelineItem
            year="2025"
            text="Launched market charts and a paper-trading simulator so anyone
            can learn by doing."
          />
          <TimelineItem
            year="Next"
            text="Rolling out research, better screeners, and—pending approvals—live
            trading with transparent pricing."
          />
        </div>
      </section>

      {/* Team (placeholders you can wire later) */}
      <section className="mx-auto max-w-6xl px-4 py-12">
        <h2 className="text-2xl font-bold">Leadership</h2>
        <div className="mt-6 grid gap-6 md:grid-cols-3">
          {[
            { name: "Your Name", role: "Founder & CEO" },
            { name: "Teammate Two", role: "Product & Design" },
            { name: "Teammate Three", role: "Engineering" },
          ].map((p) => (
            <div
              key={p.name}
              className="rounded-2xl border bg-card p-6 text-center"
            >
              <div className="mx-auto mb-4 h-20 w-20 rounded-full bg-muted" />
              <h3 className="font-semibold">{p.name}</h3>
              <p className="text-sm text-muted-foreground">{p.role}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Careers / Contact */}
      <section className="mx-auto max-w-6xl px-4 pb-16">
        <div className="grid gap-6 md:grid-cols-2">
          <div className="rounded-2xl border bg-card p-6">
            <h3 className="text-xl font-bold">Join us</h3>
            <p className="mt-2 text-muted-foreground">
              We’re a small team shipping fast. If you’re excited about
              simplifying finance, we’d love to meet you.
            </p>
            <a
              href="mailto:careers@siimply.co"
              className="mt-4 inline-block rounded-xl bg-primary px-4 py-2 text-white"
            >
              careers@siimply.co
            </a>
          </div>
          <div className="rounded-2xl border bg-card p-6">
            <h3 className="text-xl font-bold">Press & Partnerships</h3>
            <p className="mt-2 text-muted-foreground">
              For media kits, logos, or partnership ideas, drop us a note.
            </p>
            <a
              href="mailto:press@siimply.co"
              className="mt-4 inline-block rounded-xl border px-4 py-2"
            >
              press@siimply.co
            </a>
          </div>
        </div>
        <p className="mt-6 text-xs text-muted-foreground">
          Investing involves risk. Paper trading results do not reflect actual
          investment outcomes. Siimply is not a broker-dealer. Live trading will
          be available subject to regulatory approvals and partner agreements.
        </p>
      </section>
    </div>
  );
}

/* —————————————————— helpers —————————————————— */

function Stat({ kpi, label }: { kpi: string; label: string }) {
  return (
    <div className="rounded-2xl border bg-card p-6 text-center">
      <div className="text-3xl font-extrabold">{kpi}</div>
      <div className="mt-1 text-sm text-muted-foreground">{label}</div>
    </div>
  );
}

function Card({ title, body }: { title: string; body: string }) {
  return (
    <div className="rounded-2xl border bg-card p-6">
      <h3 className="font-semibold">{title}</h3>
      <p className="mt-2 text-sm text-muted-foreground">{body}</p>
    </div>
  );
}

function TimelineItem({ year, text }: { year: string; text: string }) {
  return (
    <div className="rounded-2xl border bg-card p-6">
      <div className="text-primary font-semibold">{year}</div>
      <p className="mt-2 text-sm text-muted-foreground">{text}</p>
    </div>
  );
}
